import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { FormSections, Input, ModalWindow, Spinner, TextArea, useLocalizeMessage } from 'libs.nucleus';
import { useDataViews } from '../../hooks';
export const EditDetailsModal = ({ isOpen, onClose, onSave, dataView }) => {
    const translate = useLocalizeMessage();
    const { updateDataView, checkViewNameUnique, isLoading } = useDataViews();
    const [name, setName] = useState(dataView.name || '');
    const [description, setDescription] = useState(dataView.metadata?.description || '');
    const [nameError, setNameError] = useState('');
    const [isValidatingName, setIsValidatingName] = useState(false);
    // Sync state when dataView prop changes
    useEffect(() => {
        setName(dataView.name || '');
        setDescription(dataView.metadata?.description || '');
        setNameError('');
        setIsValidatingName(false);
    }, [dataView]);
    // Create a debounced function to validate the name
    const validateName = useDebouncedCallback(async (nameValue) => {
        if (!nameValue.trim()) {
            setNameError(translate('Name is required'));
            setIsValidatingName(false);
            return;
        }
        // If name hasn't changed, no need to validate uniqueness
        if (nameValue.trim() === dataView.name) {
            setNameError('');
            setIsValidatingName(false);
            return;
        }
        setIsValidatingName(true);
        try {
            const isUnique = await checkViewNameUnique(nameValue, dataView.id);
            if (!isUnique) {
                setNameError(translate('A dataset with this name already exists'));
            }
            else {
                setNameError('');
            }
        }
        catch (error) {
            // If validation fails, allow user to proceed
            setNameError('');
        }
        finally {
            setIsValidatingName(false);
        }
    }, 500);
    // Trigger validation when name changes
    useEffect(() => {
        if (name) {
            validateName(name);
        }
        else {
            setNameError('');
        }
    }, [name, validateName]);
    const handleNameChange = useCallback((event) => {
        setName(event.target.value);
        setNameError('');
    }, []);
    const handleDescriptionChange = useCallback((event) => {
        setDescription(event.target.value);
    }, []);
    const handleSubmit = useCallback(async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError(translate('Name is required'));
            return;
        }
        // If there's a validation error, don't proceed
        if (nameError) {
            return;
        }
        // If name changed, do a final uniqueness check before submit
        if (trimmedName !== dataView.name) {
            setIsValidatingName(true);
            try {
                const isUnique = await checkViewNameUnique(trimmedName, dataView.id);
                if (!isUnique) {
                    setNameError(translate('A dataset with this name already exists'));
                    setIsValidatingName(false);
                    return;
                }
            }
            catch (error) {
                // Allow to proceed if check fails
            }
            finally {
                setIsValidatingName(false);
            }
        }
        try {
            const updatedView = await updateDataView(dataView.id, {
                name: trimmedName,
                metadata: {
                    ...dataView.metadata,
                    description: description.trim(),
                },
            });
            onSave(updatedView);
            onClose();
        }
        catch (error) {
            // Error handled in hook
        }
    }, [
        name,
        description,
        nameError,
        checkViewNameUnique,
        updateDataView,
        dataView.id,
        dataView.name,
        dataView.metadata,
        onSave,
        onClose,
        translate,
    ]);
    const saveDisabled = useMemo(() => {
        return !name.trim() || !!nameError || isValidatingName || isLoading;
    }, [name, nameError, isValidatingName, isLoading]);
    const formSections = [
        {
            title: translate('Details'),
            content: (_jsxs("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: [_jsxs("div", { className: 'relative', children: [_jsx(Input, { id: 'name', label: translate('Name'), value: name, onChange: handleNameChange, required: true, width: 'full', containLabels: true, hasError: !!nameError, errorMessage: nameError, autoComplete: 'off' }), isValidatingName && (_jsx("div", { className: 'absolute end-0 top-0', children: _jsx(Spinner, { size: 'sm' }) }))] }), _jsx(TextArea, { id: 'description', label: translate('Description'), value: description, onChange: handleDescriptionChange, width: 'full' })] })),
        },
    ];
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate('Edit dataset details'), subtitle: dataView.name, isOpen: true, closeWindow: onClose, footerPrimaryActionButton: {
            label: translate('Save'),
            onClick: handleSubmit,
            disabled: saveDisabled,
        }, footerSecondaryActionButtons: [
            {
                label: translate('Cancel'),
                onClick: onClose,
            },
        ], width: 'full', children: _jsx(FormSections, { sections: formSections }) }));
};
